import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import FullScreenButton from './FullScreenButton';
import ProcurementItemPage from './ProcurementItemPage';
import ProcurementDestinationPage from './ProcurementDestinationPage';
import ProcurementInvoicePage from './ProcurementInvoicePage';
import ProcurementShipmentListPage from './ProcurementShipmentListPage';

const TAB_KEYS = ['items', 'destinations', 'invoices', 'shipments'] as const;
type TabKey = typeof TAB_KEYS[number];

const TAB_LABELS: Record<TabKey, string> = {
  items: '품목관리',
  destinations: '입고처(공장)관리',
  invoices: '송장입력',
  shipments: '출고조회',
};

const LEGACY_PATH_TAB: Record<string, TabKey> = {
  items: 'items',
  destinations: 'destinations',
  invoices: 'invoices',
  shipments: 'shipments',
};

interface Props {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
}

function parseTab(raw: string | null): TabKey {
  if (raw && TAB_KEYS.includes(raw as TabKey)) return raw as TabKey;
  return 'items';
}

const ProcurementPage: React.FC<Props> = ({ isFullScreen, setIsFullScreen }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  const [tabIndex, setTabIndex] = useState(() => TAB_KEYS.indexOf(tab));

  useEffect(() => {
    const idx = TAB_KEYS.indexOf(tab);
    if (idx >= 0) setTabIndex(idx);
  }, [tab]);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    setTabIndex(newIndex);
    setSearchParams({ tab: TAB_KEYS[newIndex] });
  };

  const tabPanels = useMemo(() => ({
    items: (
      <ProcurementItemPage
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        embedded
      />
    ),
    destinations: (
      <ProcurementDestinationPage
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        embedded
      />
    ),
    invoices: (
      <ProcurementInvoicePage
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        embedded
      />
    ),
    shipments: <ProcurementShipmentListPage embedded />,
  }), [isFullScreen, setIsFullScreen]);

  return (
    <Box
      sx={{
        height: 'calc(100vh - 48px)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
        px: 2,
        pt: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
        <Box component="h2" sx={{ m: 0, color: '#1976d2', fontSize: 22, fontWeight: 700 }}>
          조달실
        </Box>
        <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
      </Box>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          mb: 1,
          minHeight: 44,
          '& .MuiTab-root': { fontWeight: 600, fontSize: 15, minHeight: 44 },
        }}
      >
        {TAB_KEYS.map((key) => (
          <Tab key={key} label={TAB_LABELS[key]} />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {tabPanels[TAB_KEYS[tabIndex]]}
      </Box>
    </Box>
  );
};

export { LEGACY_PATH_TAB, TAB_KEYS, parseTab };
export default ProcurementPage;
